/**
 * Public Website Page
 * Displays the dealership's public-facing website using the selected template
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Vehicle, WebsiteSettings } from '../components/website/WebsiteTemplate';
import { apiService } from '../services/api';
import { publicCopy } from './public/copy';
import PublicSiteShell from './public/PublicSiteShell';
import { PublicLanguage } from './public/types';

const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/;

function injectGoogleAnalytics(rawId: string | undefined): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const id = (rawId ?? '').trim().toUpperCase();
  if (!id || !GA_MEASUREMENT_ID_PATTERN.test(id)) return;

  // Idempotent: if a tag for this exact ID already exists, no-op.
  const existing = document.querySelector(`script[data-ga-id="${id}"]`);
  if (existing) return;

  const loader = document.createElement('script');
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  loader.setAttribute('data-ga-id', id);
  document.head.appendChild(loader);

  const inline = document.createElement('script');
  inline.setAttribute('data-ga-id', `${id}-init`);
  inline.text = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${id}');`;
  document.head.appendChild(inline);
}

const PublicWebsitePage: React.FC = () => {
  const [language, setLanguage] = useState<PublicLanguage>('en');
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const copy = publicCopy[language];
  const dealershipName = settings?.dealershipName || settings?.domain.customDomain || 'Dealership';
  const heroHeadline = settings?.hero?.headline || 'Quality Used Vehicles';
  const description =
    settings?.hero?.subheadline ||
    'Browse our inventory, financing options, and dealership information.';
  const siteUrl =
    typeof window !== 'undefined'
      ? window.location.origin
      : settings?.domain.customDomain
        ? `https://${settings.domain.customDomain}`
        : '';
  const pageTitle = `${dealershipName} | ${heroHeadline}`;
  const logoUrl = settings?.branding?.logoUrl || '/images/logo512.png';
  const metaImage =
    logoUrl.startsWith('http') || !siteUrl
      ? logoUrl
      : `${siteUrl}${logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`}`;
  const themeColor = settings?.branding?.primaryColor || '#1a3a5c';

  useEffect(() => {
    const loadWebsiteData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load website settings
        const websiteSettings = await apiService.get<WebsiteSettings>('/website/settings');
        setSettings(websiteSettings);
        injectGoogleAnalytics(websiteSettings.analytics?.gaMeasurementId);

        // Load vehicles if inventory sync is enabled
        if (websiteSettings.features.inventorySync) {
          const vehiclesData = await apiService.get<any[]>('/vehicles/public?limit=500');
          const mapped: Vehicle[] = (Array.isArray(vehiclesData) ? vehiclesData : []).map(
            (v: any) => ({
              ...v,
              price: v.asking_price,
            })
          );
          setVehicles(mapped);
        }
      } catch (err) {
        console.error('Error loading website data:', err);
        setError('Unable to load website. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadWebsiteData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{copy.common.loadingWebsite}</p>
        </div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {copy.common.websiteNotAvailable}
          </h1>
          <p className="text-gray-600">{error || copy.common.websiteSettingsMissing}</p>
        </div>
      </div>
    );
  }

  // Always use the bilingual SaaS shell for the public apex site
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="apple-mobile-web-app-title" content={dealershipName} />
        <meta name="theme-color" content={themeColor} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={metaImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>

      <PublicSiteShell
        settings={settings}
        vehicles={vehicles}
        language={language}
        onLanguageChange={setLanguage}
      />
    </>
  );
};

export default PublicWebsitePage;
